import React, { useId, useRef } from 'react';
import { Monitor, Moon, Sun, type LucideIcon } from 'lucide-react';
import { cn } from '../shadcn/lib/utils';
import { THEME_CHOICES, type ThemePreference } from './themeStore';
import { useTheme } from './useTheme';

const CHOICES: Record<ThemePreference, { label: string; icon: LucideIcon; hint: string }> = {
  light: { label: 'Light', icon: Sun, hint: 'Always light' },
  dark: { label: 'Dark', icon: Moon, hint: 'Always dark' },
  system: { label: 'System', icon: Monitor, hint: 'Match this device' },
};

/**
 * Light / Dark / System as one labelled radio group. Native-feeling keyboard: the chosen radio is the one tab stop, the
 * arrow keys move the choice (and the theme) within the group, Home and End jump to the ends. The chosen state is
 * aria-checked, so a screen reader hears "Dark, radio button, checked, 2 of 3" as focus lands. Text labels show beside
 * the icons; in the sidebar's icon rail (`group-data-[collapsible=icon]`) they fall back to screen-reader text and the
 * three stack. The colour change itself is immediate (no transition), so there is no motion to reduce; the hover tint
 * is behind motion-safe. The type size is important (!) on purpose: shell rules such as `.um button { font: inherit }` out-rank a plain utility and made the three labels overflow the rail in UMED.
 */
export default function ThemeSwitch({ className }: { className?: string }) {
  const { preference, resolved, setPreference } = useTheme();
  const labelId = useId();
  const refs = useRef<Array<HTMLButtonElement | null>>([]);

  const move = (from: number, delta: number | 'start' | 'end') => {
    const n = THEME_CHOICES.length;
    const to = delta === 'start' ? 0 : delta === 'end' ? n - 1 : (from + delta + n) % n;
    setPreference(THEME_CHOICES[to]);
    refs.current[to]?.focus();
  };

  const onKeyDown = (i: number) => (e: React.KeyboardEvent) => {
    if (e.key === 'ArrowRight' || e.key === 'ArrowDown') { e.preventDefault(); move(i, 1); }
    else if (e.key === 'ArrowLeft' || e.key === 'ArrowUp') { e.preventDefault(); move(i, -1); }
    else if (e.key === 'Home') { e.preventDefault(); move(i, 'start'); }
    else if (e.key === 'End') { e.preventDefault(); move(i, 'end'); }
  };

  return (
    <div className={cn('theme-switch', className)} data-testid="theme-switch">
      <div className="px-1 pb-1 text-xs font-medium text-sidebar-foreground/75 group-data-[collapsible=icon]:sr-only" id={labelId}>
        Theme
      </div>
      <div
        role="radiogroup"
        aria-labelledby={labelId}
        className="flex rounded-lg border border-sidebar-border bg-sidebar p-0.5 group-data-[collapsible=icon]:flex-col"
      >
        {THEME_CHOICES.map((value, i) => {
          const c = CHOICES[value];
          const Icon = c.icon;
          const on = preference === value;
          return (
            <button
              key={value}
              ref={(el) => { refs.current[i] = el; }}
              type="button"
              role="radio"
              aria-checked={on}
              tabIndex={on ? 0 : -1}
              title={value === 'system' ? `${c.hint} (now ${resolved})` : c.hint}
              onClick={() => setPreference(value)}
              onKeyDown={onKeyDown(i)}
              className={cn(
                'flex min-h-9 min-w-9 flex-1 items-center justify-center gap-1.5 rounded-md px-2 !text-xs !font-medium !leading-4 outline-none ring-sidebar-ring focus-visible:ring-2 focus-visible:ring-inset motion-safe:transition-colors',
                on
                  ? 'bg-sidebar-accent text-sidebar-accent-foreground shadow-[inset_0_0_0_1px_hsl(var(--sidebar-primary))]'
                  : 'text-sidebar-foreground hover:bg-sidebar-accent/60',
              )}
            >
              <Icon className="size-4 shrink-0" aria-hidden="true" focusable="false" />
              <span className="group-data-[collapsible=icon]:sr-only">{c.label}</span>
            </button>
          );
        })}
      </div>
    </div>
  );
}
